type KeyName =
  | 'break'
  | 'backspace'
  | 'tab'
  | 'clear'
  | 'enter'
  | 'shift'
  | 'ctrl'
  | 'alt'
  | 'pause'
  | 'capslock'
  | 'hangul'
  | 'hanja'
  | 'escape'
  | 'conversion'
  | 'non-conversion'
  | 'spacebar'
  | 'pageUp'
  | 'pageDown'
  | 'end'
  | 'home'
  | 'arrowLeft'
  | 'arrowUp'
  | 'arrowRight'
  | 'arrowDown'
  | 'select'
  | 'print'
  | 'execute'
  | 'printScreen'
  | 'insert'
  | 'delete'
  | 'help'
  | 'a';

export const KEY_MAP = new Map<KeyName, number>([
  ['break', 3],
  ['backspace', 8],
  ['tab', 9],
  ['clear', 12],
  ['enter', 13],
  ['shift', 16],
  ['ctrl', 17],
  ['alt', 18],
  ['pause', 19],
  ['capslock', 20],
  ['hangul', 21],
  ['hanja', 25],
  ['escape', 27],
  ['conversion', 28],
  ['non-conversion', 29],
  ['spacebar', 32],
  ['pageUp', 33],
  ['pageDown', 34],
  ['end', 35],
  ['home', 36],
  ['arrowLeft', 37],
  ['arrowUp', 38],
  ['arrowRight', 39],
  ['arrowDown', 40],
  ['select', 41],
  ['print', 42],
  ['execute', 43],
  ['printScreen', 44],
  ['insert', 45],
  ['delete', 46],
  ['help', 47],
  ['a', 65]
]);

type ModifierKey = 'ctrl' | 'alt' | 'shift' | 'meta';
interface SimpleKeyboardEvent {
  keyCode: number;
  which: number;
  altKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
}
export const Keys = {
  /**
   * @deprecated use oneOf instead
   */
  is(e: SimpleKeyboardEvent, expectedKey: KeyName, modifierKey?: ModifierKey) {
    return (
      (e.keyCode || e.which) === KEY_MAP.get(expectedKey) && (!modifierKey || (modifierKey && e[`${modifierKey}Key`]))
    );
  },
  /**
   * @deprecated use oneOf instead
   */
  isOneOf(e: SimpleKeyboardEvent, expectedKeys: KeyName[]) {
    return expectedKeys.map((k) => KEY_MAP.get(k)).includes(e.keyCode || e.which);
  },
  hasNoModifierKey(e: SimpleKeyboardEvent) {
    return !e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey;
  },
  hasModifierKey(e: SimpleKeyboardEvent) {
    return e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;
  },
  oneOf(e: { key: string }, ...expectedKeys: KeyboardEventKey[]): boolean {
    return expectedKeys.includes(e.key as KeyboardEventKey);
  }
};

/**
 * Full list of keys can be found here: https://www.w3.org/TR/uievents-key/
 */
type SpecialKeys = 'Unidentified';
type ModifierKeys =
  | 'Alt'
  | 'AltGraph'
  | 'CapsLock'
  | 'Control'
  | 'Fn'
  | 'FnLock'
  | 'Meta'
  | 'NumLock'
  | 'ScrollLock'
  | 'Shift'
  | 'Symbol'
  | 'SymbolLock';
type WhitespaceKeys = 'Enter' | 'Tab' | ' ';
type NavigationKeys =
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowUp'
  | 'End'
  | 'Home'
  | 'GoHome'
  | 'PageDown'
  | 'PageUp';
type EditingKeys =
  | 'Backspace'
  | 'Clear'
  | 'Copy'
  | 'CrSel'
  | 'Cut'
  | 'Delete'
  | 'EraseEof'
  | 'ExSel'
  | 'Insert'
  | 'Paste'
  | 'Redo'
  | 'Undo';
type UIKeys =
  | 'Accept'
  | 'Again'
  | 'Attn'
  | 'Cancel'
  | 'ContextMenu'
  | 'Escape'
  | 'Execute'
  | 'Find'
  | 'Help'
  | 'Pause'
  | 'MediaPause'
  | 'Play'
  | 'MediaPlay'
  | 'Props'
  | 'Select'
  | 'ZoomIn'
  | 'ZoomOut';

type FunctionKeys =
  | 'F1'
  | 'F2'
  | 'F3'
  | 'F4'
  | 'F5'
  | 'F6'
  | 'F7'
  | 'F8'
  | 'F9'
  | 'F10'
  | 'F11'
  | 'F12'
  | 'Soft1'
  | 'Soft2'
  | 'Soft3'
  | 'Soft4';
type KeyboardKeys =
  | ' '
  | '!'
  | '"'
  | '#'
  | '$'
  | '%'
  | '&'
  | "'"
  | '('
  | ')'
  | '*'
  | '+'
  | ','
  | '-'
  | '.'
  | '/'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | ':'
  | ';'
  | '<'
  | '='
  | '>'
  | '?'
  | '@'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z'
  | '['
  | '\\'
  | ']'
  | '^'
  | '_'
  | '`'
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | '{'
  | '|'
  | '}'
  | '~';

export type KeyboardEventKey =
  | SpecialKeys
  | ModifierKeys
  | WhitespaceKeys
  | NavigationKeys
  | EditingKeys
  | UIKeys
  | FunctionKeys
  | KeyboardKeys;
